import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import type {
  AssignmentCandidateImportResult,
  CanvasAssignmentCandidateInput,
} from "@vsta/contracts";

import {
  parseCanvasScheduleText,
  type ParsedCanvasSchedule,
} from "./canvas-schedule-import.js";

export type QueuePlannerInput = {
  localDate: string;
  timezone: string;
  startTime: string;
  endTime: string;
  unavailableWindows: Array<{
    label: string;
    startTime: string;
    endTime: string;
  }>;
  deferredStepIds: string[];
  courses: Array<{ id: string; title: string; position: number }>;
  units?: Array<{
    id: string;
    courseId: string;
    title: string;
    position: number;
  }>;
  assignments: Array<{
    id: string;
    courseId: string;
    unitId?: string;
    title: string;
    position: number;
    completionCondition: string;
    submissionEvidenceRequired: boolean;
    dueAt?: string;
  }>;
  resources: Array<{
    id: string;
    assignmentId: string;
    label: string;
    url?: string;
    description?: string;
  }>;
  topics: Array<{ id: string; assignmentId: string; label: string }>;
  steps: Array<{
    id: string;
    assignmentId: string;
    position: number;
    actionVerb: string;
    instruction: string;
    completionCondition: string;
    required: boolean;
    resourceIds: string[];
    topicIds: string[];
  }>;
  queueItems: Array<{
    id: string;
    stepId: string;
    position: number;
    planning: {
      mustFinish: boolean;
      studentReorderEligible?: boolean;
      minMinutes: number;
      maxMinutes: number;
    };
  }>;
};

export type QueuePlannerProps = {
  initialValue?: QueuePlannerInput;
  defaultTimezone?: string;
  initialStatus?: "draft" | "ready" | "active" | "closed";
  onPlanChange?: (input: QueuePlannerInput) => void;
  onSaveDraft: (input: QueuePlannerInput) => Promise<void>;
  onMarkReady: (input: QueuePlannerInput) => Promise<void>;
  onActivate: (input: QueuePlannerInput) => Promise<void>;
  onImportCandidates?: (
    candidates: CanvasAssignmentCandidateInput[],
  ) => Promise<AssignmentCandidateImportResult>;
  onLoadCandidates?: () => Promise<CanvasAssignmentCandidateInput[]>;
  disabled?: boolean;
};

export type QueuePlannerSubmissionValidationIssue = {
  path: Array<string | number>;
  message: string;
};

export class QueuePlannerSubmissionValidationError extends Error {
  override readonly name = "QueuePlannerSubmissionValidationError";

  constructor(readonly issues: QueuePlannerSubmissionValidationIssue[]) {
    super("One or more Daily Queue fields need attention.");
  }
}

type EditableAssignment = Omit<
  QueuePlannerInput["assignments"][number],
  "dueAt" | "position"
> & { dueAt: string };
type EditableResource = Omit<
  QueuePlannerInput["resources"][number],
  "url" | "description"
> & { url: string; description: string };
type EditableQueueItem = Omit<
  QueuePlannerInput["queueItems"][number],
  "position" | "planning"
> & {
  planning: {
    mustFinish: boolean;
    studentReorderEligible: boolean;
    minMinutes: string;
    maxMinutes: string;
  };
};
type EditableWindow = QueuePlannerInput["unavailableWindows"][number] & {
  id: string;
};
type EditablePlan = {
  localDate: string;
  timezone: string;
  startTime: string;
  endTime: string;
  unavailableWindows: EditableWindow[];
  deferredStepIds: string[];
  courses: Array<Omit<QueuePlannerInput["courses"][number], "position">>;
  units: Array<{
    id: string;
    courseId: string;
    title: string;
  }>;
  assignments: EditableAssignment[];
  resources: EditableResource[];
  topics: QueuePlannerInput["topics"];
  steps: Array<Omit<QueuePlannerInput["steps"][number], "position">>;
  queueItems: EditableQueueItem[];
};
type Action = "draft" | "ready" | "active";
type Phase = "editing" | Action | "closed";
type Undo =
  | {
      kind: "queue-remove";
      item: EditableQueueItem;
      index: number;
      deferredStepIds: string[];
    }
  | { kind: "queue-reorder"; itemId: string; originalIndex: number }
  | { kind: "window-remove"; window: EditableWindow; index: number };
type SchoolworkUndo = { plan: EditablePlan; focusId: string; message: string };

const NON_ACTION_STARTERS = new Set([
  "a",
  "an",
  "assignment",
  "chapter",
  "homework",
  "lesson",
  "notes",
  "page",
  "pages",
  "problems",
  "project",
  "quiz",
  "test",
  "the",
  "topic",
  "unit",
  "video",
  "worksheet",
]);

function newId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
    const value = Math.floor(Math.random() * 16);
    return (token === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
}

function blankPlan(defaultTimezone = ""): EditablePlan {
  const courseId = newId();
  const assignmentId = newId();
  const stepId = newId();
  return {
    localDate: "",
    timezone: defaultTimezone,
    startTime: "",
    endTime: "",
    unavailableWindows: [],
    deferredStepIds: [],
    courses: [{ id: courseId, title: "" }],
    units: [],
    assignments: [
      {
        id: assignmentId,
        courseId,
        title: "",
        completionCondition: "",
        submissionEvidenceRequired: false,
        dueAt: "",
      },
    ],
    resources: [],
    topics: [],
    steps: [
      {
        id: stepId,
        assignmentId,
        actionVerb: "",
        instruction: "",
        completionCondition: "",
        required: true,
        resourceIds: [],
        topicIds: [],
      },
    ],
    queueItems: [
      {
        id: newId(),
        stepId,
        planning: {
          mustFinish: false,
          studentReorderEligible: false,
          minMinutes: "10",
          maxMinutes: "20",
        },
      },
    ],
  };
}

function editablePlan(
  input?: QueuePlannerInput,
  defaultTimezone?: string,
): EditablePlan {
  if (input === undefined) return blankPlan(defaultTimezone);
  return {
    localDate: input.localDate,
    timezone: input.timezone,
    startTime: input.startTime,
    endTime: input.endTime,
    unavailableWindows: input.unavailableWindows.map((window) => ({
      id: newId(),
      ...window,
    })),
    deferredStepIds: [...input.deferredStepIds],
    courses: input.courses.map(({ id, title }) => ({ id, title })),
    units: (input.units ?? []).map(({ id, courseId, title }) => ({
      id,
      courseId,
      title,
    })),
    assignments: input.assignments.map((assignment) => ({
      id: assignment.id,
      courseId: assignment.courseId,
      ...(assignment.unitId === undefined ? {} : { unitId: assignment.unitId }),
      title: assignment.title,
      completionCondition: assignment.completionCondition,
      submissionEvidenceRequired: assignment.submissionEvidenceRequired,
      dueAt: assignment.dueAt ?? "",
    })),
    resources: input.resources.map((resource) => ({
      ...resource,
      url: resource.url ?? "",
      description: resource.description ?? "",
    })),
    topics: input.topics.map((topic) => ({ ...topic })),
    steps: input.steps.map((step) => ({
      id: step.id,
      assignmentId: step.assignmentId,
      actionVerb: step.actionVerb,
      instruction: step.instruction,
      completionCondition: step.completionCondition,
      required: step.required,
      resourceIds: [...step.resourceIds],
      topicIds: [...step.topicIds],
    })),
    queueItems: input.queueItems
      .slice()
      .sort((left, right) => left.position - right.position)
      .map((item) => ({
        id: item.id,
        stepId: item.stepId,
        planning: {
          mustFinish: item.planning.mustFinish,
          studentReorderEligible: item.planning.studentReorderEligible ?? false,
          minMinutes: String(item.planning.minMinutes),
          maxMinutes: String(item.planning.maxMinutes),
        },
      })),
  };
}

function localDateTimeInput(instant: string, timezone: string) {
  const values = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(new Date(instant))
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
}

function importedDueContext(dueAt: string | undefined, timezone: string) {
  if (dueAt === undefined) return "Due date needs review";
  if (timezone === "")
    return "Choose the schedule timezone to confirm this due time";
  const display = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(dueAt));
  return `Due ${display} · School time (${timezone})`;
}

function sourceItemTypeLabel(
  sourceItemType: CanvasAssignmentCandidateInput["sourceItemType"],
) {
  if (sourceItemType === "quiz") return "Quiz";
  if (sourceItemType === "discussion") return "Discussion";
  return "Assignment";
}

function localDateToday(timezone: string) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      ...(timezone === "" ? {} : { timeZone: timezone }),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(new Date())
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function isUntouchedStarterPlan(plan: EditablePlan) {
  return (
    plan.courses.length === 1 &&
    plan.courses[0]?.title === "" &&
    plan.assignments.length === 1 &&
    plan.assignments[0]?.title === "" &&
    plan.steps.length === 1 &&
    plan.steps[0]?.instruction === "" &&
    plan.queueItems.length === 1
  );
}

function serializePlan(plan: EditablePlan): QueuePlannerInput {
  return {
    localDate: plan.localDate,
    timezone: plan.timezone,
    startTime: plan.startTime,
    endTime: plan.endTime,
    unavailableWindows: plan.unavailableWindows.map((window) => ({
      label: window.label,
      startTime: window.startTime,
      endTime: window.endTime,
    })),
    deferredStepIds: [...plan.deferredStepIds],
    courses: plan.courses.map((course, index) => ({
      ...course,
      title: course.title.trim(),
      position: index + 1,
    })),
    units: plan.units.map((unit) => {
      const siblings = plan.units.filter(
        (item) => item.courseId === unit.courseId,
      );
      return {
        ...unit,
        title: unit.title.trim(),
        position: siblings.findIndex((item) => item.id === unit.id) + 1,
      };
    }),
    assignments: plan.assignments.map((assignment) => {
      const siblings = plan.assignments.filter(
        (item) => item.courseId === assignment.courseId,
      );
      return {
        id: assignment.id,
        courseId: assignment.courseId,
        title: assignment.title.trim(),
        position: siblings.findIndex((item) => item.id === assignment.id) + 1,
        completionCondition: assignment.completionCondition.trim(),
        submissionEvidenceRequired: assignment.submissionEvidenceRequired,
        ...(assignment.dueAt === "" ? {} : { dueAt: assignment.dueAt }),
      };
    }),
    resources: plan.resources.map((resource) => ({
      id: resource.id,
      assignmentId: resource.assignmentId,
      label: resource.label.trim(),
      ...(resource.url.trim() === "" ? {} : { url: resource.url.trim() }),
      ...(resource.description.trim() === ""
        ? {}
        : { description: resource.description.trim() }),
    })),
    topics: plan.topics.map((topic) => ({
      ...topic,
      label: topic.label.trim(),
    })),
    steps: plan.steps.map((step) => {
      const siblings = plan.steps.filter(
        (item) => item.assignmentId === step.assignmentId,
      );
      return {
        ...step,
        actionVerb: step.actionVerb.trim(),
        instruction: step.instruction.trim(),
        completionCondition: step.completionCondition.trim(),
        position: siblings.findIndex((item) => item.id === step.id) + 1,
      };
    }),
    queueItems: plan.queueItems.map((item, index) => ({
      id: item.id,
      stepId: item.stepId,
      position: index + 1,
      planning: {
        mustFinish: item.planning.mustFinish,
        studentReorderEligible: item.planning.studentReorderEligible,
        minMinutes: Number(item.planning.minMinutes),
        maxMinutes: Number(item.planning.maxMinutes),
      },
    })),
  };
}

function buildInput(plan: EditablePlan): {
  errors: Record<string, string>;
  input?: QueuePlannerInput;
} {
  const errors: Record<string, string> = {};
  if (plan.localDate === "") errors.localDate = "Choose a Queue date.";
  if (plan.startTime === "") errors.startTime = "Choose a start time.";
  if (plan.endTime === "") errors.endTime = "Choose an end time.";
  if (
    plan.startTime !== "" &&
    plan.endTime !== "" &&
    plan.startTime >= plan.endTime
  ) {
    errors.endTime = "End time must be after start time.";
  }
  if (plan.timezone === "") errors.timezone = "Choose a timezone.";
  if (plan.courses.length === 0) errors.courses = "Add at least one Course.";
  if (plan.assignments.length === 0)
    errors.assignments = "Add at least one Assignment.";
  if (plan.steps.length === 0) errors.steps = "Add at least one concrete Step.";
  if (plan.queueItems.length === 0)
    errors.queueItems = "Add at least one Step to the Daily Queue.";

  plan.courses.forEach((course, index) => {
    if (course.title.trim() === "")
      errors[`course.${course.id}.title`] =
        `Enter a title for Course ${index + 1}.`;
  });
  plan.units.forEach((unit, index) => {
    if (unit.title.trim() === "")
      errors[`unit.${unit.id}.title`] = `Enter a title for Unit ${index + 1}.`;
  });
  plan.assignments.forEach((assignment, index) => {
    if (assignment.title.trim() === "")
      errors[`assignment.${assignment.id}.title`] =
        `Enter a title for Assignment ${index + 1}.`;
    if (assignment.completionCondition.trim() === "")
      errors[`assignment.${assignment.id}.completion`] =
        `Enter a completion condition for Assignment ${index + 1}.`;
  });
  plan.steps.forEach((step, index) => {
    const actionVerb = step.actionVerb.trim();
    if (
      actionVerb === "" ||
      !/^\p{L}+(?:[-'’]\p{L}+)*$/u.test(actionVerb) ||
      NON_ACTION_STARTERS.has(actionVerb.toLocaleLowerCase())
    ) {
      errors[`step.${step.id}.actionVerb`] =
        "Enter one action word, such as Read, Solve, or Explain";
    }
    if (step.instruction.trim() === "")
      errors[`step.${step.id}.instruction`] =
        `Enter an instruction for Step ${index + 1}.`;
    else if (
      actionVerb !== "" &&
      step.instruction
        .trim()
        .match(/^\p{L}+(?:[-'’]\p{L}+)*/u)?.[0]
        ?.toLocaleLowerCase() !== actionVerb.toLocaleLowerCase()
    )
      errors[`step.${step.id}.instruction`] =
        `Begin Step ${index + 1} with its action verb.`;
    if (step.completionCondition.trim() === "")
      errors[`step.${step.id}.completion`] =
        `Enter a completion condition for Step ${index + 1}.`;
  });
  plan.resources.forEach((resource) => {
    if (resource.label.trim() === "")
      errors[`resource.${resource.id}.label`] = "Enter a Resource label.";
    if (resource.url !== "") {
      try {
        const parsed = new URL(resource.url);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:")
          throw new Error();
      } catch {
        errors[`resource.${resource.id}.url`] =
          "Use a valid HTTP or HTTPS URL.";
      }
    }
  });
  plan.topics.forEach((topic) => {
    if (topic.label.trim() === "")
      errors[`topic.${topic.id}.label`] = "Enter a Topic label.";
  });
  plan.queueItems.forEach((item, index) => {
    const minimum = Number(item.planning.minMinutes);
    const maximum = Number(item.planning.maxMinutes);
    if (!Number.isInteger(minimum) || minimum <= 0)
      errors[`queue.${item.id}.minimum`] =
        `Enter a positive minimum for Queue Item ${index + 1}.`;
    if (!Number.isInteger(maximum) || maximum <= 0)
      errors[`queue.${item.id}.maximum`] =
        `Enter a positive maximum for Queue Item ${index + 1}.`;
    if (minimum > 0 && maximum > 0 && minimum > maximum)
      errors[`queue.${item.id}.maximum`] =
        `Maximum must be at least the minimum for Queue Item ${index + 1}.`;
  });
  plan.unavailableWindows.forEach((window, index) => {
    if (window.label.trim() === "")
      errors[`window.${window.id}.label`] =
        `Enter a label for unavailable time ${index + 1}.`;
    if (window.startTime === "")
      errors[`window.${window.id}.start`] =
        `Choose a start for unavailable time ${index + 1}.`;
    if (window.endTime === "" || window.endTime <= window.startTime)
      errors[`window.${window.id}.end`] =
        "Unavailable time must end after it starts.";
  });
  if (Object.keys(errors).length > 0) return { errors };

  return {
    errors,
    input: {
      localDate: plan.localDate,
      timezone: plan.timezone,
      startTime: plan.startTime,
      endTime: plan.endTime,
      unavailableWindows: plan.unavailableWindows.map((window) => ({
        label: window.label,
        startTime: window.startTime,
        endTime: window.endTime,
      })),
      deferredStepIds: [...plan.deferredStepIds],
      courses: plan.courses.map((course, index) => ({
        ...course,
        title: course.title.trim(),
        position: index + 1,
      })),
      units: plan.units.map((unit) => {
        const siblings = plan.units.filter(
          (item) => item.courseId === unit.courseId,
        );
        return {
          ...unit,
          title: unit.title.trim(),
          position: siblings.findIndex((item) => item.id === unit.id) + 1,
        };
      }),
      assignments: plan.assignments
        .flatMap((assignment) => {
          const siblings = plan.assignments.filter(
            (item) => item.courseId === assignment.courseId,
          );
          return {
            ...assignment,
            title: assignment.title.trim(),
            completionCondition: assignment.completionCondition.trim(),
            position:
              siblings.findIndex((item) => item.id === assignment.id) + 1,
            ...(assignment.dueAt === "" ? { dueAt: undefined } : {}),
          };
        })
        .map(({ dueAt, ...assignment }) =>
          dueAt === undefined || dueAt === ""
            ? assignment
            : { ...assignment, dueAt },
        ),
      resources: plan.resources.map((resource) => ({
        id: resource.id,
        assignmentId: resource.assignmentId,
        label: resource.label.trim(),
        ...(resource.url.trim() === "" ? {} : { url: resource.url.trim() }),
        ...(resource.description.trim() === ""
          ? {}
          : { description: resource.description.trim() }),
      })),
      topics: plan.topics.map((topic) => ({
        ...topic,
        label: topic.label.trim(),
      })),
      steps: plan.steps.map((step) => {
        const siblings = plan.steps.filter(
          (item) => item.assignmentId === step.assignmentId,
        );
        return {
          ...step,
          actionVerb: step.actionVerb.trim(),
          instruction: step.instruction.trim(),
          completionCondition: step.completionCondition.trim(),
          position: siblings.findIndex((item) => item.id === step.id) + 1,
        };
      }),
      queueItems: plan.queueItems.map((item, index) => ({
        id: item.id,
        stepId: item.stepId,
        position: index + 1,
        planning: {
          mustFinish: item.planning.mustFinish,
          studentReorderEligible: item.planning.studentReorderEligible,
          minMinutes: Number(item.planning.minMinutes),
          maxMinutes: Number(item.planning.maxMinutes),
        },
      })),
    },
  };
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      {children}
      {error === undefined ? null : <p id={`${id}-error`}>{error}</p>}
    </div>
  );
}

function described(error: string | undefined, id: string) {
  return {
    "aria-invalid": error === undefined ? undefined : (true as const),
    "aria-describedby": error === undefined ? undefined : `${id}-error`,
  };
}

function submissionErrorKey(
  input: QueuePlannerInput,
  rawPath: Array<string | number>,
) {
  const path = rawPath[0] === "draft" ? rawPath.slice(1) : rawPath;
  const collection = path[0];
  const rawIndex = path[1];
  const index =
    typeof rawIndex === "number"
      ? rawIndex
      : Number.parseInt(rawIndex ?? "", 10);
  const field = path[2];
  if (!Number.isInteger(index) || index < 0) {
    return `submission.${path.join(".")}`;
  }
  if (collection === "courses" && field === "title") {
    const course = input.courses[index];
    return course === undefined
      ? `submission.${path.join(".")}`
      : `course.${course.id}.title`;
  }
  if (collection === "units" && field === "title") {
    const unit = input.units?.[index];
    return unit === undefined
      ? `submission.${path.join(".")}`
      : `unit.${unit.id}.title`;
  }
  if (collection === "assignments") {
    const assignment = input.assignments[index];
    if (assignment !== undefined && field === "title") {
      return `assignment.${assignment.id}.title`;
    }
    if (assignment !== undefined && field === "completionCondition") {
      return `assignment.${assignment.id}.completion`;
    }
    if (assignment !== undefined && field === "dueAt") {
      return `assignment.${assignment.id}.due`;
    }
  }
  if (collection === "steps") {
    const step = input.steps[index];
    if (step !== undefined && field === "actionVerb") {
      return `step.${step.id}.actionVerb`;
    }
    if (step !== undefined && field === "instruction") {
      return `step.${step.id}.instruction`;
    }
    if (step !== undefined && field === "completionCondition") {
      return `step.${step.id}.completion`;
    }
  }
  if (collection === "resources") {
    const resource = input.resources[index];
    if (resource !== undefined && field === "label") {
      return `resource.${resource.id}.label`;
    }
    if (resource !== undefined && field === "url") {
      return `resource.${resource.id}.url`;
    }
  }
  if (collection === "topics" && field === "label") {
    const topic = input.topics[index];
    return topic === undefined
      ? `submission.${path.join(".")}`
      : `topic.${topic.id}.label`;
  }
  if (collection === "queueItems" && field === "planning") {
    const item = input.queueItems[index];
    if (item !== undefined && path[3] === "minMinutes") {
      return `queue.${item.id}.minimum`;
    }
    if (item !== undefined && path[3] === "maxMinutes") {
      return `queue.${item.id}.maximum`;
    }
  }
  return `submission.${path.join(".")}`;
}

function fieldIdForError(formId: string, key: string) {
  if (key === "localDate") return `${formId}-local-date`;
  if (key === "startTime") return `${formId}-start-time`;
  if (key === "endTime") return `${formId}-end-time`;
  if (key === "timezone") return `${formId}-timezone`;
  const [kind, id, field] = key.split(".");
  if (id === undefined || field === undefined) return null;
  if (kind === "course") return `${formId}-course-${id}`;
  if (kind === "unit") return `${formId}-unit-${id}`;
  if (kind === "assignment") {
    return `${formId}-assignment-${id}${field === "title" ? "" : `-${field}`}`;
  }
  if (kind === "step") {
    const suffix =
      field === "instruction"
        ? ""
        : field === "actionVerb"
          ? "-action-verb"
          : `-${field}`;
    return `${formId}-step-${id}${suffix}`;
  }
  if (kind === "resource") {
    return `${formId}-resource-${id}${field === "label" ? "" : `-${field}`}`;
  }
  if (kind === "topic") return `${formId}-topic-${id}`;
  if (kind === "queue") return `${formId}-queue-${id}-${field}`;
  return null;
}

export function QueuePlanner({
  initialValue,
  defaultTimezone,
  initialStatus,
  onPlanChange,
  onSaveDraft,
  onMarkReady,
  onActivate,
  onImportCandidates,
  onLoadCandidates,
  disabled = false,
}: QueuePlannerProps) {
  const formId = useId();
  const [plan, setPlan] = useState(() =>
    editablePlan(initialValue, defaultTimezone),
  );
  const [phase, setPhase] = useState<Phase>(initialStatus ?? "editing");
  const [busy, setBusy] = useState<Action | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);
  const [schoolworkUndo, setSchoolworkUndo] = useState<SchoolworkUndo | null>(
    null,
  );
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState("");
  const [activeTab, setActiveTab] = useState<
    "import" | "schedule" | "schoolwork" | "queue"
  >(initialValue === undefined ? "import" : "schedule");
  const [parsedImport, setParsedImport] = useState<ParsedCanvasSchedule | null>(
    null,
  );
  const [importFileName, setImportFileName] = useState("");
  const [importFailure, setImportFailure] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importSaved, setImportSaved] = useState(false);
  const [importAdded, setImportAdded] = useState(false);
  const [selectedImportKeys, setSelectedImportKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [importSearch, setImportSearch] = useState("");
  const [importDate, setImportDate] = useState("all");
  const importFileChosen = useRef(false);
  const importLoadAttempted = useRef(false);
  const errorSummary = useRef<HTMLDivElement>(null);

  const plannerTabs = [
    { id: "import", label: "1 Import" },
    { id: "schedule", label: "2 Day & time" },
    { id: "schoolwork", label: "3 Details" },
    { id: "queue", label: "4 Review" },
  ] as const;

  function selectTabFromKey(
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) {
    let nextIndex: number;
    if (event.key === "ArrowRight")
      nextIndex = (currentIndex + 1) % plannerTabs.length;
    else if (event.key === "ArrowLeft")
      nextIndex = (currentIndex - 1 + plannerTabs.length) % plannerTabs.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = plannerTabs.length - 1;
    else return;
    event.preventDefault();
    const next = plannerTabs[nextIndex];
    if (next === undefined) return;
    setActiveTab(next.id);
    document.getElementById(`${formId}-tab-${next.id}`)?.focus();
  }

  async function readCanvasFile(file: File) {
    importFileChosen.current = true;
    setImportFileName(file.name);
    setImportFailure(null);
    setImportMessage(null);
    setImportSaved(false);
    setImportAdded(false);
    setSelectedImportKeys(new Set());
    setImportSearch("");
    setImportDate("all");
    if (file.size > 10 * 1024 * 1024) {
      setParsedImport(null);
      setImportFailure("Choose a saved Canvas page smaller than 10 MB.");
      return;
    }
    try {
      const parsed = parseCanvasScheduleText({
        fileName: file.name,
        text: await file.text(),
        ...(plan.timezone === "" ? {} : { timezone: plan.timezone }),
      });
      setParsedImport(parsed);
      if (parsed.candidates.length === 0) {
        setImportFailure(
          "No Canvas assignments were found. Try the Calendar Agenda or List view.",
        );
      }
    } catch (reason) {
      setParsedImport(null);
      setImportFailure(
        reason instanceof Error
          ? reason.message
          : "That saved Canvas page could not be read.",
      );
    }
  }

  useEffect(() => {
    if (onLoadCandidates === undefined || importLoadAttempted.current) return;
    importLoadAttempted.current = true;
    let cancelled = false;
    void onLoadCandidates()
      .then((candidates) => {
        if (cancelled || importFileChosen.current || candidates.length === 0)
          return;
        const normalizedCandidates: ParsedCanvasSchedule["candidates"] =
          candidates.map((candidate) => ({
            sourceItemKey: candidate.sourceItemKey,
            sourceCourseKey: candidate.sourceCourseKey,
            courseTitle: candidate.courseTitle,
            title: candidate.title,
            ...(candidate.courseColor === undefined
              ? {}
              : { courseColor: candidate.courseColor }),
            ...(candidate.sourceItemType === undefined
              ? {}
              : { sourceItemType: candidate.sourceItemType }),
            ...(candidate.dueAt === undefined
              ? {}
              : { dueAt: candidate.dueAt }),
            ...(candidate.sourceUrl === undefined
              ? {}
              : { sourceUrl: candidate.sourceUrl }),
          }));
        setParsedImport({
          format: "html",
          candidates: normalizedCandidates,
          duplicatesRemoved: 0,
          warnings: [],
        });
        setImportSaved(true);
        setImportMessage(
          "Your previous import is ready. Choose the activities needed today.",
        );
      })
      .catch(() => {
        if (!cancelled && !importFileChosen.current) {
          setImportFailure(
            "Previously imported Canvas activities could not be loaded. You can upload the saved page again.",
          );
        }
      });
    return () => {
      cancelled = true;
      importLoadAttempted.current = false;
    };
  }, [onLoadCandidates]);

  async function saveImportedCandidates() {
    if (parsedImport === null || parsedImport.candidates.length === 0) return;
    if (onImportCandidates === undefined) {
      setImportFailure("Schedule import is temporarily unavailable.");
      return;
    }
    setImportBusy(true);
    setImportFailure(null);
    try {
      const result = await onImportCandidates(parsedImport.candidates);
      setImportSaved(true);
      setImportMessage(
        `${result.totalCount} Canvas activit${result.totalCount === 1 ? "y" : "ies"} imported and available on the Student Console. No additional planning is required to see them.`,
      );
    } catch (reason) {
      setImportFailure(
        reason instanceof Error
          ? reason.message
          : "The imported assignments could not be saved.",
      );
    } finally {
      setImportBusy(false);
    }
  }

  function addImportToPlan() {
    if (
      parsedImport === null ||
      selectedImportKeys.size === 0 ||
      !importSaved ||
      importAdded ||
      isLocked ||
      disabled
    )
      return;
    const courseIds = new Map<string, string>();
    change((current) => {
      const replaceStarter = isUntouchedStarterPlan(current);
      const courses = replaceStarter ? [] : [...current.courses];
      const assignments = replaceStarter ? [] : [...current.assignments];
      const steps = replaceStarter ? [] : [...current.steps];
      const queueItems = replaceStarter ? [] : [...current.queueItems];
      for (const candidate of parsedImport.candidates.filter((candidate) =>
        selectedImportKeys.has(candidate.sourceItemKey),
      )) {
        let courseId = courseIds.get(candidate.sourceCourseKey);
        if (courseId === undefined) {
          const existing = courses.find(
            (course) => course.title === candidate.courseTitle,
          );
          courseId = existing?.id ?? newId();
          courseIds.set(candidate.sourceCourseKey, courseId);
          if (existing === undefined)
            courses.push({ id: courseId, title: candidate.courseTitle });
        }
        const assignmentId = newId();
        assignments.push({
          id: assignmentId,
          courseId,
          title: candidate.title,
          completionCondition: "Finish this Canvas activity.",
          submissionEvidenceRequired: false,
          dueAt:
            candidate.dueAt === undefined || current.timezone === ""
              ? ""
              : localDateTimeInput(candidate.dueAt, current.timezone),
        });
        const stepId = newId();
        steps.push({
          id: stepId,
          assignmentId,
          actionVerb: "Complete",
          instruction: `Complete ${candidate.title}`,
          completionCondition: "The Canvas activity is finished.",
          required: true,
          resourceIds: [],
          topicIds: [],
        });
        queueItems.push({
          id: newId(),
          stepId,
          planning: {
            mustFinish: false,
            studentReorderEligible: false,
            minMinutes: "10",
            maxMinutes: "20",
          },
        });
      }
      return {
        ...current,
        localDate:
          current.localDate === ""
            ? localDateToday(current.timezone)
            : current.localDate,
        startTime: current.startTime === "" ? "08:00" : current.startTime,
        endTime: current.endTime === "" ? "15:00" : current.endTime,
        courses,
        assignments,
        steps,
        queueItems,
      };
    });
    setImportAdded(true);
    setActiveTab("queue");
    setMessage(
      `Added ${selectedImportKeys.size} simple Queue Item${selectedImportKeys.size === 1 ? "" : "s"}. Review ${selectedImportKeys.size === 1 ? "it" : "them"}, then save for later or start the queue.`,
    );
  }

  useEffect(() => {
    if (pendingFocus === null) return;
    document.getElementById(pendingFocus)?.focus();
    setPendingFocus(null);
  }, [pendingFocus, plan]);

  useEffect(() => {
    onPlanChange?.(serializePlan(plan));
  }, [onPlanChange, plan]);

  useEffect(() => {
    if (initialStatus !== undefined) setPhase(initialStatus);
  }, [initialStatus]);

  function change(
    update: (current: EditablePlan) => EditablePlan,
    keys: string[] = [],
    preserveSchoolworkUndo = false,
  ) {
    setPlan(update);
    if (!preserveSchoolworkUndo) setSchoolworkUndo(null);
    setPhase("editing");
    setMessage(null);
    setFailure(null);
    if (keys.length > 0) {
      setErrors((current) => {
        const next = { ...current };
        keys.forEach((key) => delete next[key]);
        return next;
      });
    }
  }

  function addCourse() {
    const courseId = newId();
    const assignmentId = newId();
    const stepId = newId();
    change((current) => ({
      ...current,
      courses: [...current.courses, { id: courseId, title: "" }],
      assignments: [
        ...current.assignments,
        {
          id: assignmentId,
          courseId,
          title: "",
          completionCondition: "",
          submissionEvidenceRequired: false,
          dueAt: "",
        },
      ],
      steps: [
        ...current.steps,
        {
          id: stepId,
          assignmentId,
          actionVerb: "",
          instruction: "",
          completionCondition: "",
          required: true,
          resourceIds: [],
          topicIds: [],
        },
      ],
      queueItems: [
        ...current.queueItems,
        {
          id: newId(),
          stepId,
          planning: {
            mustFinish: false,
            studentReorderEligible: false,
            minMinutes: "10",
            maxMinutes: "20",
          },
        },
      ],
    }));
    setPendingFocus(`${formId}-course-${courseId}`);
  }

  function addAssignment(courseId: string) {
    const assignmentId = newId();
    const stepId = newId();
    change((current) => ({
      ...current,
      assignments: [
        ...current.assignments,
        {
          id: assignmentId,
          courseId,
          title: "",
          completionCondition: "",
          submissionEvidenceRequired: false,
          dueAt: "",
        },
      ],
      steps: [
        ...current.steps,
        {
          id: stepId,
          assignmentId,
          actionVerb: "",
          instruction: "",
          completionCondition: "",
          required: true,
          resourceIds: [],
          topicIds: [],
        },
      ],
      queueItems: [
        ...current.queueItems,
        {
          id: newId(),
          stepId,
          planning: {
            mustFinish: false,
            studentReorderEligible: false,
            minMinutes: "10",
            maxMinutes: "20",
          },
        },
      ],
    }));
    setPendingFocus(`${formId}-assignment-${assignmentId}`);
  }

  function addUnit(courseId: string) {
    const unitId = newId();
    change((current) => ({
      ...current,
      units: [...current.units, { id: unitId, courseId, title: "" }],
    }));
    setPendingFocus(`${formId}-unit-${unitId}`);
  }

  function removeUnit(unitId: string) {
    const unit = plan.units.find((candidate) => candidate.id === unitId);
    const addUnitTarget =
      unit === undefined ? null : `${formId}-add-unit-${unit.courseId}`;
    change(
      (current) => ({
        ...current,
        units: current.units.filter((unit) => unit.id !== unitId),
        assignments: current.assignments.map((assignment) => {
          if (assignment.unitId !== unitId) return assignment;
          const { unitId: _removed, ...withoutUnit } = assignment;
          void _removed;
          return withoutUnit;
        }),
      }),
      [],
      true,
    );
    if (addUnitTarget !== null) setPendingFocus(addUnitTarget);
    setMessage("Unit removed. Its Assignments remain in the Course.");
  }

  function addStep(assignmentId: string) {
    const stepId = newId();
    change((current) => ({
      ...current,
      steps: [
        ...current.steps,
        {
          id: stepId,
          assignmentId,
          actionVerb: "",
          instruction: "",
          completionCondition: "",
          required: true,
          resourceIds: [],
          topicIds: [],
        },
      ],
      queueItems: [
        ...current.queueItems,
        {
          id: newId(),
          stepId,
          planning: {
            mustFinish: false,
            studentReorderEligible: false,
            minMinutes: "10",
            maxMinutes: "20",
          },
        },
      ],
    }));
    setPendingFocus(`${formId}-step-${stepId}-action-verb`);
  }

  function moveStep(assignmentId: string, index: number, direction: -1 | 1) {
    const siblings = plan.steps.filter(
      (step) => step.assignmentId === assignmentId,
    );
    const current = siblings[index];
    const destination = siblings[index + direction];
    if (current === undefined || destination === undefined) return;
    setSchoolworkUndo({
      plan,
      focusId: `${formId}-step-${current.id}`,
      message: "Step order restored.",
    });
    change(
      (value) => {
        const steps = [...value.steps];
        const currentIndex = steps.findIndex((step) => step.id === current.id);
        const destinationIndex = steps.findIndex(
          (step) => step.id === destination.id,
        );
        [steps[currentIndex], steps[destinationIndex]] = [
          steps[destinationIndex]!,
          steps[currentIndex]!,
        ];
        return { ...value, steps };
      },
      [],
      true,
    );
    setPendingFocus(`${formId}-step-${current.id}`);
    setMessage(`Step ${index + 1} moved.`);
  }

  function removeStep(assignmentId: string, index: number) {
    const siblings = plan.steps.filter(
      (step) => step.assignmentId === assignmentId,
    );
    const step = siblings[index];
    if (step === undefined) return;
    const next = siblings[index + 1] ?? siblings[index - 1];
    const focusId =
      next === undefined
        ? `${formId}-assignment-${assignmentId}-add-step`
        : `${formId}-step-${next.id}`;
    setSchoolworkUndo({ plan, focusId, message: "Removed Step restored." });
    change(
      (current) => ({
        ...current,
        steps: current.steps.filter((item) => item.id !== step.id),
        queueItems: current.queueItems.filter(
          (item) => item.stepId !== step.id,
        ),
        deferredStepIds: current.deferredStepIds.filter((id) => id !== step.id),
      }),
      [],
      true,
    );
    setPendingFocus(focusId);
    setMessage(`Step ${index + 1} removed. You can undo this change.`);
  }

  function removeResource(
    assignmentId: string,
    resourceId: string,
    index: number,
  ) {
    const siblings = plan.resources.filter(
      (resource) => resource.assignmentId === assignmentId,
    );
    const next = siblings[index + 1] ?? siblings[index - 1];
    const focusId =
      next === undefined
        ? `${formId}-assignment-${assignmentId}-add-resource`
        : `${formId}-resource-${next.id}`;
    setSchoolworkUndo({ plan, focusId, message: "Removed Resource restored." });
    change(
      (current) => ({
        ...current,
        resources: current.resources.filter((item) => item.id !== resourceId),
        steps: current.steps.map((item) => ({
          ...item,
          resourceIds: item.resourceIds.filter((id) => id !== resourceId),
        })),
      }),
      [],
      true,
    );
    setPendingFocus(focusId);
    setMessage("Resource removed. You can undo this change.");
  }

  function removeTopic(assignmentId: string, topicId: string, index: number) {
    const siblings = plan.topics.filter(
      (topic) => topic.assignmentId === assignmentId,
    );
    const next = siblings[index + 1] ?? siblings[index - 1];
    const focusId =
      next === undefined
        ? `${formId}-assignment-${assignmentId}-add-topic`
        : `${formId}-topic-${next.id}`;
    setSchoolworkUndo({
      plan,
      focusId,
      message: "Removed Topic restored.",
    });
    change(
      (current) => ({
        ...current,
        topics: current.topics.filter((item) => item.id !== topicId),
        steps: current.steps.map((item) => ({
          ...item,
          topicIds: item.topicIds.filter((id) => id !== topicId),
        })),
      }),
      [],
      true,
    );
    setPendingFocus(focusId);
    setMessage("Topic removed. You can undo this change.");
  }

  function undoSchoolwork() {
    if (schoolworkUndo === null) return;
    setPlan(schoolworkUndo.plan);
    setPhase("editing");
    setFailure(null);
    setMessage(schoolworkUndo.message);
    setPendingFocus(schoolworkUndo.focusId);
    setSchoolworkUndo(null);
  }

  function removeAssignment(assignmentId: string) {
    const assignment = plan.assignments.find(
      (item) => item.id === assignmentId,
    );
    if (assignment === undefined) return;
    const siblings = plan.assignments.filter(
      (item) => item.courseId === assignment.courseId,
    );
    const index = siblings.findIndex((item) => item.id === assignmentId);
    const next = siblings[index + 1] ?? siblings[index - 1];
    const focusId =
      next === undefined
        ? `${formId}-course-${assignment.courseId}-add-assignment`
        : `${formId}-assignment-${next.id}`;
    const stepIds = new Set(
      plan.steps
        .filter((step) => step.assignmentId === assignmentId)
        .map((step) => step.id),
    );
    setSchoolworkUndo({
      plan,
      focusId,
      message: "Removed Assignment restored.",
    });
    change(
      (current) => ({
        ...current,
        assignments: current.assignments.filter(
          (item) => item.id !== assignmentId,
        ),
        resources: current.resources.filter(
          (item) => item.assignmentId !== assignmentId,
        ),
        topics: current.topics.filter(
          (item) => item.assignmentId !== assignmentId,
        ),
        steps: current.steps.filter(
          (item) => item.assignmentId !== assignmentId,
        ),
        queueItems: current.queueItems.filter(
          (item) => !stepIds.has(item.stepId),
        ),
        deferredStepIds: current.deferredStepIds.filter(
          (stepId) => !stepIds.has(stepId),
        ),
      }),
      [],
      true,
    );
    setPendingFocus(focusId);
    setMessage("Assignment removed. You can undo this change.");
  }

  function removeCourse(courseId: string) {
    const index = plan.courses.findIndex((item) => item.id === courseId);
    if (index < 0) return;
    const next = plan.courses[index + 1] ?? plan.courses[index - 1];
    const focusId =
      next === undefined
        ? `${formId}-add-course`
        : `${formId}-course-${next.id}`;
    const assignmentIds = new Set(
      plan.assignments
        .filter((item) => item.courseId === courseId)
        .map((item) => item.id),
    );
    const stepIds = new Set(
      plan.steps
        .filter((item) => assignmentIds.has(item.assignmentId))
        .map((item) => item.id),
    );
    setSchoolworkUndo({
      plan,
      focusId,
      message: "Removed Course restored.",
    });
    change(
      (current) => ({
        ...current,
        courses: current.courses.filter((item) => item.id !== courseId),
        units: current.units.filter((item) => item.courseId !== courseId),
        assignments: current.assignments.filter(
          (item) => !assignmentIds.has(item.id),
        ),
        resources: current.resources.filter(
          (item) => !assignmentIds.has(item.assignmentId),
        ),
        topics: current.topics.filter(
          (item) => !assignmentIds.has(item.assignmentId),
        ),
        steps: current.steps.filter(
          (item) => !assignmentIds.has(item.assignmentId),
        ),
        queueItems: current.queueItems.filter(
          (item) => !stepIds.has(item.stepId),
        ),
        deferredStepIds: current.deferredStepIds.filter(
          (stepId) => !stepIds.has(stepId),
        ),
      }),
      [],
      true,
    );
    setPendingFocus(focusId);
    setMessage("Course removed. You can undo this change.");
  }

  function moveQueueItem(index: number, direction: -1 | 1) {
    const destination = index + direction;
    if (destination < 0 || destination >= plan.queueItems.length) return;
    const items = [...plan.queueItems];
    const current = items[index];
    const next = items[destination];
    if (current === undefined || next === undefined) return;
    [items[index], items[destination]] = [next, current];
    setUndo({
      kind: "queue-reorder",
      itemId: current.id,
      originalIndex: index,
    });
    change((value) => ({ ...value, queueItems: items }));
    setMessage(`Queue Item ${index + 1} moved.`);
  }

  function removeQueueItem(index: number, action: "defer" | "remove") {
    const item = plan.queueItems[index];
    if (item === undefined) return;
    const nextItem = plan.queueItems[index + 1] ?? plan.queueItems[index - 1];
    setUndo({
      kind: "queue-remove",
      item,
      index,
      deferredStepIds: [...plan.deferredStepIds],
    });
    setSelectedStepId("");
    change((current) => ({
      ...current,
      queueItems: current.queueItems.filter((value) => value.id !== item.id),
      deferredStepIds:
        action === "defer"
          ? [...current.deferredStepIds, item.stepId]
          : current.deferredStepIds.filter((stepId) => stepId !== item.stepId),
    }));
    setPendingFocus(
      nextItem === undefined
        ? `${formId}-queue-step`
        : `${formId}-queue-${nextItem.id}-minimum`,
    );
    setMessage(
      action === "defer"
        ? `Queue Item ${index + 1} deferred. You can undo this change.`
        : `Queue Item ${index + 1} removed from today. You can undo this change.`,
    );
  }

  function addQueueItem() {
    if (selectedStepId === "") return;
    const itemId = newId();
    change((current) => ({
      ...current,
      queueItems: [
        ...current.queueItems,
        {
          id: itemId,
          stepId: selectedStepId,
          planning: {
            mustFinish: false,
            studentReorderEligible: false,
            minMinutes: "10",
            maxMinutes: "20",
          },
        },
      ],
      deferredStepIds: current.deferredStepIds.filter(
        (stepId) => stepId !== selectedStepId,
      ),
    }));
    setUndo(null);
    setSelectedStepId("");
    setPendingFocus(`${formId}-queue-${itemId}-minimum`);
    setMessage("Queue Item added.");
  }

  function undoChange() {
    if (undo === null) return;
    setSelectedStepId("");
    const restoredItemId = undo.kind === "queue-remove" ? undo.item.id : null;
    const restoredWindowId =
      undo.kind === "window-remove" ? undo.window.id : null;
    change((current) => {
      if (undo.kind === "window-remove") {
        const windows = [...current.unavailableWindows];
        windows.splice(undo.index, 0, undo.window);
        return { ...current, unavailableWindows: windows };
      }
      const items = [...current.queueItems];
      if (undo.kind === "queue-remove") items.splice(undo.index, 0, undo.item);
      else {
        const currentIndex = items.findIndex((item) => item.id === undo.itemId);
        const [item] = items.splice(currentIndex, 1);
        if (item !== undefined) items.splice(undo.originalIndex, 0, item);
      }
      return {
        ...current,
        queueItems: items,
        ...(undo.kind === "queue-remove"
          ? { deferredStepIds: undo.deferredStepIds }
          : {}),
      };
    });
    if (restoredItemId !== null) {
      setPendingFocus(`${formId}-queue-${restoredItemId}-minimum`);
    }
    if (restoredWindowId !== null) {
      setPendingFocus(`${formId}-window-${restoredWindowId}-label`);
    }
    setUndo(null);
    setMessage("Latest change undone.");
  }

  async function perform(action: Action) {
    if (disabled) {
      setFailure(
        "Live state is not confirmed. Your entries remain here; reconnect before publishing this Daily Queue change.",
      );
      return;
    }
    const result = buildInput(plan);
    setErrors(result.errors);
    setFailure(null);
    setMessage(null);
    if (result.input === undefined) {
      const firstKey = Object.keys(result.errors)[0];
      if (firstKey !== undefined) {
        setActiveTab(
          firstKey === "localDate" ||
            firstKey === "startTime" ||
            firstKey === "endTime" ||
            firstKey === "timezone" ||
            firstKey.startsWith("window.")
            ? "schedule"
            : firstKey.startsWith("queue.") || firstKey === "queueItems"
              ? "queue"
              : "schoolwork",
        );
      }
      window.setTimeout(() => errorSummary.current?.focus(), 0);
      return;
    }
    const input = result.input;
    setBusy(action);
    try {
      if (action === "draft") await onSaveDraft(input);
      if (action === "ready") await onMarkReady(input);
      if (action === "active") await onActivate(input);
      setPhase(action);
      setMessage(
        action === "draft"
          ? "Saved for later. You can keep editing this Daily Queue."
          : action === "ready"
            ? "Review finished. Start the queue when the student is ready."
            : "Daily Queue is Active.",
      );
    } catch (reason) {
      if (reason instanceof QueuePlannerSubmissionValidationError) {
        const submissionErrors = Object.fromEntries(
          reason.issues.map((issue) => [
            submissionErrorKey(input, issue.path),
            issue.message,
          ]),
        );
        setErrors(submissionErrors);
        const firstField = Object.keys(submissionErrors)
          .map((key) => fieldIdForError(formId, key))
          .find((id) => id !== null);
        const firstKey = Object.keys(submissionErrors)[0];
        if (firstKey !== undefined) {
          setActiveTab(
            firstKey.startsWith("queue.") || firstKey === "queueItems"
              ? "queue"
              : firstKey === "localDate" ||
                  firstKey === "startTime" ||
                  firstKey === "endTime" ||
                  firstKey === "timezone" ||
                  firstKey.startsWith("window.")
                ? "schedule"
                : "schoolwork",
          );
        }
        if (firstField !== undefined) setPendingFocus(firstField);
        setFailure(null);
      } else {
        setFailure(
          reason instanceof Error
            ? reason.message
            : "The Daily Queue could not be updated. Try again.",
        );
      }
    } finally {
      setBusy(null);
    }
  }

  const courseNumber = new Map(
    plan.courses.map((course, index) => [course.id, index + 1]),
  );
  const assignmentNumber = new Map(
    plan.assignments.map((assignment, index) => [assignment.id, index + 1]),
  );
  const stepNumber = new Map(
    plan.steps.map((step, index) => [step.id, index + 1]),
  );
  const isBusy = busy !== null;
  const isLocked =
    phase === "ready" || phase === "active" || phase === "closed";
  const errorEntries = Object.entries(errors);
  const rollupFor = (required: boolean | null) => {
    const items = plan.queueItems.filter((item) => {
      if (required === null) return true;
      return (
        plan.steps.find((step) => step.id === item.stepId)?.required ===
        required
      );
    });
    return {
      itemCount: items.length,
      mustFinishCount: items.filter((item) => item.planning.mustFinish).length,
      minMinutes: items.reduce(
        (total, item) => total + (Number(item.planning.minMinutes) || 0),
        0,
      ),
      maxMinutes: items.reduce(
        (total, item) => total + (Number(item.planning.maxMinutes) || 0),
        0,
      ),
    };
  };
  const liveRollups = [
    ["Required", rollupFor(true)],
    ["Optional", rollupFor(false)],
    ["Total", rollupFor(null)],
  ] as const;
  const scheduleFields = [
    {
      suffix: "local-date",
      label: "Queue date",
      type: "date",
      key: "localDate",
    },
    {
      suffix: "start-time",
      label: "Start time",
      type: "time",
      key: "startTime",
    },
    { suffix: "end-time", label: "End time", type: "time", key: "endTime" },
  ] as const;
  const sourceDate = (candidate: CanvasAssignmentCandidateInput) =>
    candidate.dueAt === undefined
      ? "undated"
      : localDateTimeInput(
          candidate.dueAt,
          plan.timezone || "America/Chicago",
        ).slice(0, 10);
  const importDates = [
    ...new Set(parsedImport?.candidates.map(sourceDate) ?? []),
  ].sort();
  const visibleImportCandidates =
    parsedImport?.candidates.filter((candidate) => {
      if (importDate !== "all" && sourceDate(candidate) !== importDate)
        return false;
      const query = importSearch.trim().toLocaleLowerCase();
      if (query === "") return true;
      return `${candidate.title} ${candidate.courseTitle} ${sourceItemTypeLabel(candidate.sourceItemType)}`
        .toLocaleLowerCase()
        .includes(query);
    }) ?? [];

  return (
    <section aria-labelledby={`${formId}-title`}>
      <h2 id={`${formId}-title`}>Build the Daily Queue</h2>
      <p>
        Choose from saved schoolwork or import a saved Canvas page. Review the
        day, time and work, then save a Daily Queue. Nothing starts while you
        plan.
      </p>
      {errorEntries.length === 0 ? null : (
        <div ref={errorSummary} role="alert" tabIndex={-1}>
          <h3>A few details are still needed</h3>
          <p>{errorEntries[0]?.[1]}</p>
          <p>Fix this item, then try again. Your work is still here.</p>
        </div>
      )}
      {failure === null ? null : (
        <div role="alert">
          <strong>The Daily Queue was not changed.</strong> {failure}
        </div>
      )}
      <p role="status" aria-live="polite">
        {busy === "draft"
          ? "Saving for later…"
          : busy === "ready"
            ? "Finishing review…"
            : busy === "active"
              ? "Starting queue…"
              : message}
      </p>

      <form
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void perform("draft");
        }}
      >
        <div
          className="planner-tabs"
          role="tablist"
          aria-label="Daily Queue builder sections"
        >
          {plannerTabs.map((tab, index) => (
            <button
              key={tab.id}
              id={`${formId}-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`${formId}-panel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => selectTabFromKey(event, index)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <section
          id={`${formId}-panel-import`}
          className="planner-tab-panel import-panel"
          role="tabpanel"
          aria-labelledby={`${formId}-tab-import`}
          inert={activeTab !== "import"}
        >
          <div className="import-intro">
            <p className="eyebrow">Schoolwork for planning</p>
            <h3>
              {importSaved
                ? "Choose from your saved activities"
                : "Bring in schoolwork from Canvas"}
            </h3>
            <p>
              Saved activities are Assignment Candidates awaiting your review.
              Their source dates do not set a work time. Choose what belongs in
              a Daily Queue, then review the day and time before saving.
            </p>
          </div>
          {isLocked ? (
            <p role="status">
              This Daily Queue has already been published. You can browse saved
              activities, but cannot add them to this Queue.
            </p>
          ) : null}
          <details open={parsedImport === null}>
            <summary>How to import or update schoolwork</summary>
            <ol>
              <li>
                In Canvas Calendar, open Agenda or List view for the dates you
                need.
              </li>
              <li>
                Save that calendar page as HTML, HTM or MHTML, then choose the
                file below.
              </li>
              <li>
                Review the preview and select Import. Then choose activities for
                a Daily Queue.
              </li>
            </ol>
            <p>
              The saved page is read on this device. Only planning details are
              saved here. This does not open assignments or quizzes. This is a
              saved copy, not a live connection to Canvas.
            </p>
            <label className="file-drop" htmlFor={`${formId}-canvas-file`}>
              <span>Choose a saved Canvas page</span>
              <small>HTML, HTM, or MHTML · up to 10 MB</small>
              <input
                id={`${formId}-canvas-file`}
                type="file"
                accept=".html,.htm,.mhtml,text/html,multipart/related"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) void readCanvasFile(file);
                }}
              />
            </label>
          </details>
          {importFileName === "" ? null : (
            <p className="file-name">Selected: {importFileName}</p>
          )}
          {importFailure === null ? null : <p role="alert">{importFailure}</p>}
          {parsedImport === null ||
          parsedImport.candidates.length === 0 ? null : (
            <div className="import-preview">
              <div>
                <strong>
                  {importFileName === "" && importSaved
                    ? `${parsedImport.candidates.length} previously imported Canvas ${parsedImport.candidates.length === 1 ? "activity" : "activities"} available`
                    : `${parsedImport.candidates.length} Canvas ${parsedImport.candidates.length === 1 ? "activity" : "activities"} found`}
                </strong>
                <span>
                  {parsedImport.duplicatesRemoved} duplicate
                  {parsedImport.duplicatesRemoved === 1 ? "" : "s"}{" "}
                  consolidated; the latest entry wins.
                </span>
                <p className="import-advisory">
                  Canvas did not provide every planning detail. That will not
                  block this import.
                </p>
              </div>
              {parsedImport.warnings.length === 0 ? null : (
                <ul>
                  {parsedImport.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}
              {importSaved && parsedImport.candidates.length > 1 ? (
                <div className="import-filters">
                  <label className="import-search">
                    <span>Find an activity</span>
                    <input
                      type="search"
                      value={importSearch}
                      placeholder="Search title or Course"
                      onChange={(event) => setImportSearch(event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Filter by source date</span>
                    <select
                      value={importDate}
                      onChange={(event) => setImportDate(event.target.value)}
                    >
                      <option value="all">All dates</option>
                      {importDates.map((date) => (
                        <option key={date} value={date}>
                          {date === "undated" ? "No source date" : date}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}
              {importSaved ? (
                <div className="import-selection">
                  <p role="status">
                    {selectedImportKeys.size} selected across all dates.
                    Selection is not yet saved to a Daily Queue.
                  </p>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={
                      disabled ||
                      isLocked ||
                      importAdded ||
                      visibleImportCandidates.length === 0
                    }
                    onClick={() =>
                      setSelectedImportKeys(
                        (current) =>
                          new Set([
                            ...current,
                            ...visibleImportCandidates.map(
                              (candidate) => candidate.sourceItemKey,
                            ),
                          ]),
                      )
                    }
                  >
                    Select visible activities
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={
                      disabled ||
                      isLocked ||
                      importAdded ||
                      selectedImportKeys.size === 0
                    }
                    onClick={() => setSelectedImportKeys(new Set())}
                  >
                    Clear selection
                  </button>
                </div>
              ) : null}
              <div
                className="import-preview-list"
                aria-label="Imported assignment preview"
              >
                {visibleImportCandidates.map((candidate) => (
                  <article
                    key={candidate.sourceItemKey}
                    style={
                      candidate.courseColor === undefined
                        ? undefined
                        : { borderInlineStartColor: candidate.courseColor }
                    }
                  >
                    {importSaved ? (
                      <label className="import-choice">
                        <input
                          type="checkbox"
                          disabled={disabled || isLocked || importAdded}
                          aria-label={`Add ${candidate.title} to today's queue`}
                          checked={selectedImportKeys.has(
                            candidate.sourceItemKey,
                          )}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;
                            setSelectedImportKeys((current) => {
                              const next = new Set(current);
                              if (checked) next.add(candidate.sourceItemKey);
                              else next.delete(candidate.sourceItemKey);
                              return next;
                            });
                          }}
                        />
                        <span>Add to today</span>
                      </label>
                    ) : null}
                    <div className="import-course-context">
                      {candidate.courseColor === undefined ? null : (
                        <span
                          className="course-color-swatch"
                          style={{ backgroundColor: candidate.courseColor }}
                          aria-hidden="true"
                        />
                      )}
                      <span>{candidate.courseTitle}</span>
                      <small>
                        {sourceItemTypeLabel(candidate.sourceItemType)}
                      </small>
                    </div>
                    <strong>{candidate.title}</strong>
                    <time dateTime={candidate.dueAt}>
                      {importedDueContext(candidate.dueAt, plan.timezone)}
                    </time>
                  </article>
                ))}
                {visibleImportCandidates.length === 0 ? (
                  <p className="import-no-results">
                    No imported activities match these filters. Choose All dates
                    or clear your search.
                  </p>
                ) : null}
              </div>
              <div className="import-actions">
                <button
                  type="button"
                  disabled={importBusy || importSaved || disabled}
                  onClick={() => void saveImportedCandidates()}
                >
                  {importBusy
                    ? "Importing…"
                    : importSaved
                      ? "Imported"
                      : `Import ${parsedImport.candidates.length} ${parsedImport.candidates.length === 1 ? "activity" : "activities"}`}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={
                    !importSaved ||
                    selectedImportKeys.size === 0 ||
                    importAdded ||
                    disabled ||
                    isLocked
                  }
                  onClick={addImportToPlan}
                >
                  {importAdded
                    ? "Added to today's queue"
                    : `Add ${selectedImportKeys.size} selected to today's queue`}
                </button>
              </div>
              {importMessage === null ? null : (
                <p role="status">{importMessage}</p>
              )}
            </div>
          )}
        </section>

        <section
          id={`${formId}-panel-schedule`}
          className="planner-tab-panel"
          role="tabpanel"
          aria-labelledby={`${formId}-tab-schedule`}
          inert={activeTab !== "schedule"}
        >
          <fieldset disabled={isBusy || isLocked}>
            <legend>Daily schedule</legend>
            <p className="full-width schedule-guidance">
              Imported queues use today and 8:00 AM–3:00 PM as a starting point.
              Change these only if the student’s day is different.
            </p>
            {scheduleFields.map(({ suffix, label, type, key }) => {
              const id = `${formId}-${suffix}`;
              const value = plan[key];
              const error = errors[key];
              return (
                <Field key={key} id={id} label={label} error={error}>
                  <input
                    id={id}
                    type={type}
                    required
                    value={value}
                    {...described(error, id)}
                    onChange={(event) =>
                      change(
                        (current) => ({
                          ...current,
                          [key]: event.target.value,
                        }),
                        [key],
                      )
                    }
                  />
                </Field>
              );
            })}
            <Field
              id={`${formId}-timezone`}
              label="Timezone"
              error={errors.timezone}
            >
              <select
                id={`${formId}-timezone`}
                required
                value={plan.timezone}
                {...described(errors.timezone, `${formId}-timezone`)}
                onChange={(event) =>
                  change(
                    (current) => ({ ...current, timezone: event.target.value }),
                    ["timezone"],
                  )
                }
              >
                <option value="">Choose timezone</option>
                <option value="America/Chicago">Central time</option>
                <option value="America/Denver">Mountain time</option>
                <option value="America/Los_Angeles">Pacific time</option>
                <option value="America/New_York">Eastern time</option>
              </select>
            </Field>
            <section
              className="full-width"
              aria-labelledby={`${formId}-unavailable-title`}
            >
              <h3 id={`${formId}-unavailable-title`}>Unavailable time</h3>
              {plan.unavailableWindows.map((window, index) => {
                const number = index + 1;
                return (
                  <fieldset key={window.id}>
                    <legend>Unavailable time {number}</legend>
                    {(["label", "startTime", "endTime"] as const).map((key) => {
                      const suffix =
                        key === "startTime"
                          ? "start"
                          : key === "endTime"
                            ? "end"
                            : key;
                      const id = `${formId}-window-${window.id}-${suffix}`;
                      const error = errors[`window.${window.id}.${suffix}`];
                      return (
                        <Field
                          key={key}
                          id={id}
                          label={`Unavailable time ${number} ${suffix}`}
                          error={error}
                        >
                          <input
                            id={id}
                            type={key === "label" ? "text" : "time"}
                            required
                            value={window[key]}
                            {...described(error, id)}
                            onChange={(event) =>
                              change(
                                (current) => ({
                                  ...current,
                                  unavailableWindows:
                                    current.unavailableWindows.map((item) =>
                                      item.id === window.id
                                        ? { ...item, [key]: event.target.value }
                                        : item,
                                    ),
                                }),
                                [`window.${window.id}.${suffix}`],
                              )
                            }
                          />
                        </Field>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        const next =
                          plan.unavailableWindows[index + 1] ??
                          plan.unavailableWindows[index - 1];
                        setUndo({ kind: "window-remove", window, index });
                        change((current) => ({
                          ...current,
                          unavailableWindows: current.unavailableWindows.filter(
                            (item) => item.id !== window.id,
                          ),
                        }));
                        setPendingFocus(
                          next === undefined
                            ? `${formId}-add-unavailable`
                            : `${formId}-window-${next.id}-label`,
                        );
                      }}
                    >
                      Remove unavailable time {number}
                    </button>
                  </fieldset>
                );
              })}
              <button
                id={`${formId}-add-unavailable`}
                type="button"
                onClick={() => {
                  const id = newId();
                  change((current) => ({
                    ...current,
                    unavailableWindows: [
                      ...current.unavailableWindows,
                      { id, label: "", startTime: "", endTime: "" },
                    ],
                  }));
                  setPendingFocus(`${formId}-window-${id}-label`);
                }}
              >
                Add unavailable time
              </button>
              {undo?.kind === "window-remove" ? (
                <button type="button" onClick={undoChange}>
                  Undo removed unavailable time
                </button>
              ) : null}
            </section>
          </fieldset>
        </section>

        <section
          id={`${formId}-panel-schoolwork`}
          className="planner-tab-panel"
          role="tabpanel"
          aria-labelledby={`${formId}-tab-schoolwork`}
          inert={activeTab !== "schoolwork"}
        >
          <h3 id={`${formId}-schoolwork-title`}>Courses and Assignments</h3>
          <p>
            Simple imported activities are already filled in. Edit this section
            only when an activity needs more detailed Steps, Resources, or
            Topics.
          </p>
          {plan.courses.map((course) => {
            const number = courseNumber.get(course.id) ?? 0;
            const courseUnits = plan.units.filter(
              (item) => item.courseId === course.id,
            );
            const courseAssignments = plan.assignments.filter(
              (item) => item.courseId === course.id,
            );
            const courseId = `${formId}-course-${course.id}`;
            return (
              <fieldset
                key={course.id}
                className="course-editor"
                disabled={isBusy || isLocked}
              >
                <legend>Course {number}</legend>
                <Field
                  id={courseId}
                  label={`Course ${number} title`}
                  error={errors[`course.${course.id}.title`]}
                >
                  <input
                    id={courseId}
                    required
                    value={course.title}
                    {...described(
                      errors[`course.${course.id}.title`],
                      courseId,
                    )}
                    onChange={(event) =>
                      change(
                        (current) => ({
                          ...current,
                          courses: current.courses.map((item) =>
                            item.id === course.id
                              ? { ...item, title: event.target.value }
                              : item,
                          ),
                        }),
                        [`course.${course.id}.title`],
                      )
                    }
                  />
                </Field>
                <section aria-label={`Course ${number} Units`}>
                  <h4>Units (optional)</h4>
                  {courseUnits.map((unit, unitIndex) => {
                    const unitId = `${formId}-unit-${unit.id}`;
                    return (
                      <div key={unit.id} className="unit-editor">
                        <Field
                          id={unitId}
                          label={`Course ${number} Unit ${unitIndex + 1} title`}
                          error={errors[`unit.${unit.id}.title`]}
                        >
                          <input
                            id={unitId}
                            required
                            value={unit.title}
                            {...described(
                              errors[`unit.${unit.id}.title`],
                              unitId,
                            )}
                            onChange={(event) =>
                              change(
                                (current) => ({
                                  ...current,
                                  units: current.units.map((item) =>
                                    item.id === unit.id
                                      ? { ...item, title: event.target.value }
                                      : item,
                                  ),
                                }),
                                [`unit.${unit.id}.title`],
                              )
                            }
                          />
                        </Field>
                        <button
                          type="button"
                          onClick={() => removeUnit(unit.id)}
                        >
                          Remove Unit {unitIndex + 1} from Course {number}
                        </button>
                      </div>
                    );
                  })}
                  <button
                    id={`${formId}-add-unit-${course.id}`}
                    type="button"
                    onClick={() => addUnit(course.id)}
                  >
                    Add Unit to Course {number}
                  </button>
                </section>
                {courseAssignments.map((assignment) => {
                  const aNumber = assignmentNumber.get(assignment.id) ?? 0;
                  const assignmentSteps = plan.steps.filter(
                    (item) => item.assignmentId === assignment.id,
                  );
                  const assignmentResources = plan.resources.filter(
                    (item) => item.assignmentId === assignment.id,
                  );
                  const assignmentTopics = plan.topics.filter(
                    (item) => item.assignmentId === assignment.id,
                  );
                  const assignmentId = `${formId}-assignment-${assignment.id}`;
                  return (
                    <fieldset key={assignment.id} className="assignment-editor">
                      <legend>Assignment {aNumber}</legend>
                      <Field
                        id={assignmentId}
                        label={`Assignment ${aNumber} title`}
                        error={errors[`assignment.${assignment.id}.title`]}
                      >
                        <input
                          id={assignmentId}
                          required
                          value={assignment.title}
                          {...described(
                            errors[`assignment.${assignment.id}.title`],
                            assignmentId,
                          )}
                          onChange={(event) =>
                            change(
                              (current) => ({
                                ...current,
                                assignments: current.assignments.map((item) =>
                                  item.id === assignment.id
                                    ? { ...item, title: event.target.value }
                                    : item,
                                ),
                              }),
                              [`assignment.${assignment.id}.title`],
                            )
                          }
                        />
                      </Field>
                      <Field
                        id={`${assignmentId}-unit`}
                        label={`Assignment ${aNumber} Unit (optional)`}
                      >
                        <select
                          id={`${assignmentId}-unit`}
                          value={assignment.unitId ?? ""}
                          onChange={(event) =>
                            change((current) => ({
                              ...current,
                              assignments: current.assignments.map((item) => {
                                if (item.id !== assignment.id) return item;
                                if (event.target.value === "") {
                                  const { unitId: _removed, ...withoutUnit } =
                                    item;
                                  void _removed;
                                  return withoutUnit;
                                }
                                return { ...item, unitId: event.target.value };
                              }),
                            }))
                          }
                        >
                          <option value="">No Unit</option>
                          {courseUnits.map((unit, index) => (
                            <option key={unit.id} value={unit.id}>
                              Unit {index + 1}: {unit.title || "Untitled Unit"}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field
                        id={`${assignmentId}-completion`}
                        label={`Assignment ${aNumber} completion condition`}
                        error={errors[`assignment.${assignment.id}.completion`]}
                      >
                        <input
                          id={`${assignmentId}-completion`}
                          required
                          value={assignment.completionCondition}
                          {...described(
                            errors[`assignment.${assignment.id}.completion`],
                            `${assignmentId}-completion`,
                          )}
                          onChange={(event) =>
                            change(
                              (current) => ({
                                ...current,
                                assignments: current.assignments.map((item) =>
                                  item.id === assignment.id
                                    ? {
                                        ...item,
                                        completionCondition: event.target.value,
                                      }
                                    : item,
                                ),
                              }),
                              [`assignment.${assignment.id}.completion`],
                            )
                          }
                        />
                      </Field>
                      <Field
                        id={`${assignmentId}-due`}
                        label={`Assignment ${aNumber} due date and time (optional)`}
                        error={errors[`assignment.${assignment.id}.due`]}
                      >
                        <input
                          id={`${assignmentId}-due`}
                          type="datetime-local"
                          value={assignment.dueAt}
                          {...described(
                            errors[`assignment.${assignment.id}.due`],
                            `${assignmentId}-due`,
                          )}
                          onChange={(event) =>
                            change(
                              (current) => ({
                                ...current,
                                assignments: current.assignments.map((item) =>
                                  item.id === assignment.id
                                    ? { ...item, dueAt: event.target.value }
                                    : item,
                                ),
                              }),
                              [`assignment.${assignment.id}.due`],
                            )
                          }
                        />
                      </Field>
                      <label>
                        <input
                          type="checkbox"
                          checked={assignment.submissionEvidenceRequired}
                          onChange={(event) =>
                            change((current) => ({
                              ...current,
                              assignments: current.assignments.map((item) =>
                                item.id === assignment.id
                                  ? {
                                      ...item,
                                      submissionEvidenceRequired:
                                        event.target.checked,
                                    }
                                  : item,
                              ),
                            }))
                          }
                        />{" "}
                        Assignment {aNumber} requires Submission Evidence
                      </label>

                      <section
                        className="assignment-resources"
                        aria-label={`Assignment ${aNumber} Resources`}
                      >
                        <h4>Assignment Resources</h4>
                        <p>
                          Edit each Resource once, then link it to any Step.
                        </p>
                        {assignmentResources.map((resource, resourceIndex) => {
                          const rNumber = resourceIndex + 1;
                          const resourceId = `${formId}-resource-${resource.id}`;
                          return (
                            <fieldset key={resource.id}>
                              <legend>Resource {rNumber}</legend>
                              <Field
                                id={resourceId}
                                label={`Assignment ${aNumber} Resource ${rNumber} label`}
                                error={errors[`resource.${resource.id}.label`]}
                              >
                                <input
                                  id={resourceId}
                                  required
                                  value={resource.label}
                                  {...described(
                                    errors[`resource.${resource.id}.label`],
                                    resourceId,
                                  )}
                                  onChange={(event) =>
                                    change(
                                      (current) => ({
                                        ...current,
                                        resources: current.resources.map(
                                          (item) =>
                                            item.id === resource.id
                                              ? {
                                                  ...item,
                                                  label: event.target.value,
                                                }
                                              : item,
                                        ),
                                      }),
                                      [`resource.${resource.id}.label`],
                                    )
                                  }
                                />
                              </Field>
                              <Field
                                id={`${resourceId}-url`}
                                label={`Assignment ${aNumber} Resource ${rNumber} URL (optional)`}
                                error={errors[`resource.${resource.id}.url`]}
                              >
                                <input
                                  id={`${resourceId}-url`}
                                  type="url"
                                  value={resource.url}
                                  {...described(
                                    errors[`resource.${resource.id}.url`],
                                    `${resourceId}-url`,
                                  )}
                                  onChange={(event) =>
                                    change(
                                      (current) => ({
                                        ...current,
                                        resources: current.resources.map(
                                          (item) =>
                                            item.id === resource.id
                                              ? {
                                                  ...item,
                                                  url: event.target.value,
                                                }
                                              : item,
                                        ),
                                      }),
                                      [`resource.${resource.id}.url`],
                                    )
                                  }
                                />
                              </Field>
                              <Field
                                id={`${resourceId}-description`}
                                label={`Assignment ${aNumber} Resource ${rNumber} description (optional)`}
                              >
                                <textarea
                                  id={`${resourceId}-description`}
                                  value={resource.description}
                                  onChange={(event) =>
                                    change((current) => ({
                                      ...current,
                                      resources: current.resources.map(
                                        (item) =>
                                          item.id === resource.id
                                            ? {
                                                ...item,
                                                description: event.target.value,
                                              }
                                            : item,
                                      ),
                                    }))
                                  }
                                />
                              </Field>
                              <button
                                type="button"
                                onClick={() =>
                                  removeResource(
                                    assignment.id,
                                    resource.id,
                                    resourceIndex,
                                  )
                                }
                              >
                                Remove Assignment {aNumber} Resource {rNumber}
                              </button>
                            </fieldset>
                          );
                        })}
                        <button
                          id={`${formId}-assignment-${assignment.id}-add-resource`}
                          type="button"
                          onClick={() => {
                            const id = newId();
                            change((current) => ({
                              ...current,
                              resources: [
                                ...current.resources,
                                {
                                  id,
                                  assignmentId: assignment.id,
                                  label: "",
                                  url: "",
                                  description: "",
                                },
                              ],
                            }));
                            setPendingFocus(`${formId}-resource-${id}`);
                          }}
                        >
                          Add Resource to Assignment {aNumber}
                        </button>
                      </section>

                      <section
                        className="assignment-topics"
                        aria-label={`Assignment ${aNumber} Topics`}
                      >
                        <h4>Assignment Topics</h4>
                        <p>Edit each Topic once, then link it to any Step.</p>
                        {assignmentTopics.map((topic, topicIndex) => {
                          const tNumber = topicIndex + 1;
                          const topicId = `${formId}-topic-${topic.id}`;
                          return (
                            <div key={topic.id} className="topic-editor">
                              <Field
                                id={topicId}
                                label={`Assignment ${aNumber} Topic ${tNumber} label`}
                                error={errors[`topic.${topic.id}.label`]}
                              >
                                <input
                                  id={topicId}
                                  required
                                  value={topic.label}
                                  {...described(
                                    errors[`topic.${topic.id}.label`],
                                    topicId,
                                  )}
                                  onChange={(event) =>
                                    change(
                                      (current) => ({
                                        ...current,
                                        topics: current.topics.map((item) =>
                                          item.id === topic.id
                                            ? {
                                                ...item,
                                                label: event.target.value,
                                              }
                                            : item,
                                        ),
                                      }),
                                      [`topic.${topic.id}.label`],
                                    )
                                  }
                                />
                              </Field>
                              <button
                                type="button"
                                onClick={() =>
                                  removeTopic(
                                    assignment.id,
                                    topic.id,
                                    topicIndex,
                                  )
                                }
                              >
                                Remove Assignment {aNumber} Topic {tNumber}
                              </button>
                            </div>
                          );
                        })}
                        <button
                          id={`${formId}-assignment-${assignment.id}-add-topic`}
                          type="button"
                          onClick={() => {
                            const id = newId();
                            change((current) => ({
                              ...current,
                              topics: [
                                ...current.topics,
                                {
                                  id,
                                  assignmentId: assignment.id,
                                  label: "",
                                },
                              ],
                            }));
                            setPendingFocus(`${formId}-topic-${id}`);
                          }}
                        >
                          Add Topic to Assignment {aNumber}
                        </button>
                      </section>

                      {assignmentSteps.map((step, stepIndex) => {
                        const sNumber = stepNumber.get(step.id) ?? 0;
                        const stepId = `${formId}-step-${step.id}`;
                        const linkedResources: EditableResource[] = [];
                        return (
                          <fieldset key={step.id} className="step-editor">
                            <legend>Step {sNumber}</legend>
                            <Field
                              id={`${stepId}-action-verb`}
                              label={`Step ${sNumber} action verb`}
                              error={errors[`step.${step.id}.actionVerb`]}
                            >
                              <input
                                id={`${stepId}-action-verb`}
                                required
                                maxLength={80}
                                autoComplete="off"
                                value={step.actionVerb}
                                {...described(
                                  errors[`step.${step.id}.actionVerb`],
                                  `${stepId}-action-verb`,
                                )}
                                onChange={(event) =>
                                  change(
                                    (current) => ({
                                      ...current,
                                      steps: current.steps.map((item) =>
                                        item.id === step.id
                                          ? {
                                              ...item,
                                              actionVerb: event.target.value,
                                            }
                                          : item,
                                      ),
                                    }),
                                    [
                                      `step.${step.id}.actionVerb`,
                                      `step.${step.id}.instruction`,
                                    ],
                                  )
                                }
                              />
                            </Field>
                            <p className="field-guidance">
                              Use one action word that tells the student what to
                              do, such as Read, Solve, Write, or Submit. Start
                              the instruction with the same word.
                            </p>
                            <Field
                              id={stepId}
                              label={`Step ${sNumber} instruction`}
                              error={errors[`step.${step.id}.instruction`]}
                            >
                              <input
                                id={stepId}
                                required
                                value={step.instruction}
                                {...described(
                                  errors[`step.${step.id}.instruction`],
                                  stepId,
                                )}
                                onChange={(event) =>
                                  change(
                                    (current) => ({
                                      ...current,
                                      steps: current.steps.map((item) =>
                                        item.id === step.id
                                          ? {
                                              ...item,
                                              instruction: event.target.value,
                                            }
                                          : item,
                                      ),
                                    }),
                                    [`step.${step.id}.instruction`],
                                  )
                                }
                              />
                            </Field>
                            <Field
                              id={`${stepId}-completion`}
                              label={`Step ${sNumber} completion condition`}
                              error={errors[`step.${step.id}.completion`]}
                            >
                              <input
                                id={`${stepId}-completion`}
                                required
                                value={step.completionCondition}
                                {...described(
                                  errors[`step.${step.id}.completion`],
                                  `${stepId}-completion`,
                                )}
                                onChange={(event) =>
                                  change(
                                    (current) => ({
                                      ...current,
                                      steps: current.steps.map((item) =>
                                        item.id === step.id
                                          ? {
                                              ...item,
                                              completionCondition:
                                                event.target.value,
                                            }
                                          : item,
                                      ),
                                    }),
                                    [`step.${step.id}.completion`],
                                  )
                                }
                              />
                            </Field>
                            <label>
                              <input
                                type="checkbox"
                                checked={step.required}
                                onChange={(event) =>
                                  change((current) => ({
                                    ...current,
                                    steps: current.steps.map((item) =>
                                      item.id === step.id
                                        ? {
                                            ...item,
                                            required: event.target.checked,
                                          }
                                        : item,
                                    ),
                                  }))
                                }
                              />{" "}
                              Step {sNumber} is required
                            </label>
                            <section
                              className="support-editor"
                              aria-label={`Step ${sNumber} supporting Resources and Topics`}
                            >
                              {linkedResources.map(
                                (resource, resourceIndex) => {
                                  const rNumber = resourceIndex + 1;
                                  const resourceId = `${formId}-resource-${resource.id}`;
                                  return (
                                    <fieldset key={resource.id}>
                                      <legend>
                                        Step {sNumber} Resource {rNumber}
                                      </legend>
                                      <Field
                                        id={resourceId}
                                        label={`Step ${sNumber} Resource ${rNumber} label`}
                                        error={
                                          errors[
                                            `resource.${resource.id}.label`
                                          ]
                                        }
                                      >
                                        <input
                                          id={resourceId}
                                          required
                                          value={resource.label}
                                          {...described(
                                            errors[
                                              `resource.${resource.id}.label`
                                            ],
                                            resourceId,
                                          )}
                                          onChange={(event) =>
                                            change(
                                              (current) => ({
                                                ...current,
                                                resources:
                                                  current.resources.map(
                                                    (item) =>
                                                      item.id === resource.id
                                                        ? {
                                                            ...item,
                                                            label:
                                                              event.target
                                                                .value,
                                                          }
                                                        : item,
                                                  ),
                                              }),
                                              [`resource.${resource.id}.label`],
                                            )
                                          }
                                        />
                                      </Field>
                                      <Field
                                        id={`${resourceId}-url`}
                                        label={`Step ${sNumber} Resource ${rNumber} URL (optional)`}
                                        error={
                                          errors[`resource.${resource.id}.url`]
                                        }
                                      >
                                        <input
                                          id={`${resourceId}-url`}
                                          type="url"
                                          value={resource.url}
                                          {...described(
                                            errors[
                                              `resource.${resource.id}.url`
                                            ],
                                            `${resourceId}-url`,
                                          )}
                                          onChange={(event) =>
                                            change(
                                              (current) => ({
                                                ...current,
                                                resources:
                                                  current.resources.map(
                                                    (item) =>
                                                      item.id === resource.id
                                                        ? {
                                                            ...item,
                                                            url: event.target
                                                              .value,
                                                          }
                                                        : item,
                                                  ),
                                              }),
                                              [`resource.${resource.id}.url`],
                                            )
                                          }
                                        />
                                      </Field>
                                      <Field
                                        id={`${resourceId}-description`}
                                        label={`Step ${sNumber} Resource ${rNumber} description (optional)`}
                                      >
                                        <textarea
                                          id={`${resourceId}-description`}
                                          value={resource.description}
                                          onChange={(event) =>
                                            change((current) => ({
                                              ...current,
                                              resources: current.resources.map(
                                                (item) =>
                                                  item.id === resource.id
                                                    ? {
                                                        ...item,
                                                        description:
                                                          event.target.value,
                                                      }
                                                    : item,
                                              ),
                                            }))
                                          }
                                        />
                                      </Field>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          change((current) => ({
                                            ...current,
                                            resources: current.resources.filter(
                                              (item) => item.id !== resource.id,
                                            ),
                                            steps: current.steps.map((item) =>
                                              item.id === step.id
                                                ? {
                                                    ...item,
                                                    resourceIds:
                                                      item.resourceIds.filter(
                                                        (id) =>
                                                          id !== resource.id,
                                                      ),
                                                  }
                                                : item,
                                            ),
                                          }))
                                        }
                                      >
                                        Remove Resource {rNumber} from Step{" "}
                                        {sNumber}
                                      </button>
                                    </fieldset>
                                  );
                                },
                              )}
                              {assignmentResources.map((resource) => (
                                <label key={resource.id}>
                                  <input
                                    type="checkbox"
                                    checked={step.resourceIds.includes(
                                      resource.id,
                                    )}
                                    onChange={(event) =>
                                      change((current) => ({
                                        ...current,
                                        steps: current.steps.map((item) =>
                                          item.id === step.id
                                            ? {
                                                ...item,
                                                resourceIds: event.target
                                                  .checked
                                                  ? [
                                                      ...item.resourceIds,
                                                      resource.id,
                                                    ]
                                                  : item.resourceIds.filter(
                                                      (id) =>
                                                        id !== resource.id,
                                                    ),
                                              }
                                            : item,
                                        ),
                                      }))
                                    }
                                  />{" "}
                                  Link {resource.label || "untitled Resource"}{" "}
                                  to Step {sNumber}
                                </label>
                              ))}
                              {assignmentTopics.map((topic) => (
                                <label key={topic.id}>
                                  <input
                                    type="checkbox"
                                    checked={step.topicIds.includes(topic.id)}
                                    onChange={(event) =>
                                      change((current) => ({
                                        ...current,
                                        steps: current.steps.map((item) =>
                                          item.id === step.id
                                            ? {
                                                ...item,
                                                topicIds: event.target.checked
                                                  ? [...item.topicIds, topic.id]
                                                  : item.topicIds.filter(
                                                      (id) => id !== topic.id,
                                                    ),
                                              }
                                            : item,
                                        ),
                                      }))
                                    }
                                  />{" "}
                                  Link {topic.label || "untitled Topic"} to Step{" "}
                                  {sNumber}
                                </label>
                              ))}
                            </section>
                            <div
                              className="step-actions"
                              aria-label={`Reorder Step ${sNumber}`}
                            >
                              <button
                                type="button"
                                disabled={stepIndex === 0}
                                onClick={() =>
                                  moveStep(assignment.id, stepIndex, -1)
                                }
                              >
                                Move Step {sNumber} up
                              </button>
                              <button
                                type="button"
                                disabled={
                                  stepIndex === assignmentSteps.length - 1
                                }
                                onClick={() =>
                                  moveStep(assignment.id, stepIndex, 1)
                                }
                              >
                                Move Step {sNumber} down
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  removeStep(assignment.id, stepIndex)
                                }
                              >
                                Remove Step {sNumber}
                              </button>
                            </div>
                          </fieldset>
                        );
                      })}
                      <button
                        id={`${formId}-assignment-${assignment.id}-add-step`}
                        type="button"
                        onClick={() => addStep(assignment.id)}
                      >
                        Add Step to Assignment {aNumber}
                      </button>
                      {plan.assignments.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => removeAssignment(assignment.id)}
                        >
                          Remove Assignment {aNumber}
                        </button>
                      ) : null}
                    </fieldset>
                  );
                })}
                <button
                  id={`${formId}-course-${course.id}-add-assignment`}
                  type="button"
                  onClick={() => addAssignment(course.id)}
                >
                  Add Assignment to Course {number}
                </button>
                {plan.courses.length > 1 ? (
                  <button type="button" onClick={() => removeCourse(course.id)}>
                    Remove Course {number}
                  </button>
                ) : null}
              </fieldset>
            );
          })}
          <button
            id={`${formId}-add-course`}
            type="button"
            disabled={isBusy || isLocked}
            onClick={addCourse}
          >
            Add Course
          </button>
          {schoolworkUndo === null ? null : (
            <button
              type="button"
              disabled={isBusy || isLocked}
              onClick={undoSchoolwork}
            >
              Undo last schoolwork change
            </button>
          )}
        </section>

        <section
          id={`${formId}-panel-queue`}
          className="planner-tab-panel"
          role="tabpanel"
          aria-labelledby={`${formId}-tab-queue`}
          inert={activeTab !== "queue"}
        >
          <h3 id={`${formId}-queue-title`}>Daily Queue order</h3>
          <p>
            This is what the student will work through. Dragging is not
            required—use the move buttons only when the order needs changing.
          </p>
          <section className="planner-rollups" aria-label="Daily Queue totals">
            {liveRollups.map(([label, rollup]) => (
              <div key={label}>
                <strong>{label}</strong>
                <span>
                  {rollup.itemCount} item{rollup.itemCount === 1 ? "" : "s"}
                </span>
                <span>{rollup.mustFinishCount} must finish</span>
                <span>
                  {rollup.minMinutes}–{rollup.maxMinutes} minutes
                </span>
              </div>
            ))}
          </section>
          {errors.queueItems === undefined ? null : (
            <p role="alert">{errors.queueItems}</p>
          )}
          {plan.queueItems.map((item, index) => {
            const number = index + 1;
            const step = plan.steps.find((value) => value.id === item.stepId);
            const assignment = plan.assignments.find(
              (value) => value.id === step?.assignmentId,
            );
            const course = plan.courses.find(
              (value) => value.id === assignment?.courseId,
            );
            const prefix = `${formId}-queue-${item.id}`;
            return (
              <fieldset
                key={item.id}
                className="queue-item-editor"
                disabled={isBusy || isLocked}
              >
                <legend>Queue Item {number}</legend>
                <p>
                  <strong>
                    {step?.instruction ||
                      `Step ${stepNumber.get(item.stepId) ?? ""}`}
                  </strong>
                  <br />
                  {course?.title || "Untitled Course"} ·{" "}
                  {assignment?.title || "Untitled Assignment"}
                </p>
                <Field
                  id={`${prefix}-minimum`}
                  label={`Queue Item ${number} planned minimum minutes`}
                  error={errors[`queue.${item.id}.minimum`]}
                >
                  <input
                    id={`${prefix}-minimum`}
                    type="number"
                    inputMode="numeric"
                    min="1"
                    required
                    value={item.planning.minMinutes}
                    {...described(
                      errors[`queue.${item.id}.minimum`],
                      `${prefix}-minimum`,
                    )}
                    onChange={(event) =>
                      change(
                        (current) => ({
                          ...current,
                          queueItems: current.queueItems.map((value) =>
                            value.id === item.id
                              ? {
                                  ...value,
                                  planning: {
                                    ...value.planning,
                                    minMinutes: event.target.value,
                                  },
                                }
                              : value,
                          ),
                        }),
                        [`queue.${item.id}.minimum`],
                      )
                    }
                  />
                </Field>
                <Field
                  id={`${prefix}-maximum`}
                  label={`Queue Item ${number} planned maximum minutes`}
                  error={errors[`queue.${item.id}.maximum`]}
                >
                  <input
                    id={`${prefix}-maximum`}
                    type="number"
                    inputMode="numeric"
                    min="1"
                    required
                    value={item.planning.maxMinutes}
                    {...described(
                      errors[`queue.${item.id}.maximum`],
                      `${prefix}-maximum`,
                    )}
                    onChange={(event) =>
                      change(
                        (current) => ({
                          ...current,
                          queueItems: current.queueItems.map((value) =>
                            value.id === item.id
                              ? {
                                  ...value,
                                  planning: {
                                    ...value.planning,
                                    maxMinutes: event.target.value,
                                  },
                                }
                              : value,
                          ),
                        }),
                        [`queue.${item.id}.maximum`],
                      )
                    }
                  />
                </Field>
                <label>
                  <input
                    type="checkbox"
                    checked={item.planning.mustFinish}
                    onChange={(event) =>
                      change((current) => ({
                        ...current,
                        queueItems: current.queueItems.map((value) =>
                          value.id === item.id
                            ? {
                                ...value,
                                planning: {
                                  ...value.planning,
                                  mustFinish: event.target.checked,
                                },
                              }
                            : value,
                        ),
                      }))
                    }
                  />{" "}
                  Queue Item {number} must finish today
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={item.planning.studentReorderEligible}
                    onChange={(event) =>
                      change((current) => ({
                        ...current,
                        queueItems: current.queueItems.map((value) =>
                          value.id === item.id
                            ? {
                                ...value,
                                planning: {
                                  ...value.planning,
                                  studentReorderEligible: event.target.checked,
                                },
                              }
                            : value,
                        ),
                      }))
                    }
                  />{" "}
                  Student may reorder Queue Item {number}
                </label>
                <div aria-label={`Reorder Queue Item ${number}`}>
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => moveQueueItem(index, -1)}
                  >
                    Move Queue Item {number} up
                  </button>
                  <button
                    type="button"
                    disabled={index === plan.queueItems.length - 1}
                    onClick={() => moveQueueItem(index, 1)}
                  >
                    Move Queue Item {number} down
                  </button>
                  <button
                    type="button"
                    onClick={() => removeQueueItem(index, "defer")}
                  >
                    Defer Queue Item {number}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeQueueItem(index, "remove")}
                  >
                    Remove Queue Item {number}
                  </button>
                </div>
              </fieldset>
            );
          })}
          {plan.deferredStepIds.length === 0 ? null : (
            <section
              className="deferred-steps"
              aria-labelledby={`${formId}-deferred-title`}
            >
              <h4 id={`${formId}-deferred-title`}>Deferred Steps</h4>
              <p>
                These Steps stay in the plan but are intentionally deferred.
              </p>
              <ul>
                {plan.deferredStepIds.map((stepId) => {
                  const step = plan.steps.find((value) => value.id === stepId);
                  return (
                    <li key={stepId}>
                      <strong>{step?.instruction || "Untitled Step"}</strong>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
          {plan.steps.some(
            (step) => !plan.queueItems.some((item) => item.stepId === step.id),
          ) ? (
            <div className="queue-add-row">
              <label htmlFor={`${formId}-queue-step`}>
                Step to add to Daily Queue
              </label>
              <select
                id={`${formId}-queue-step`}
                disabled={isBusy || isLocked}
                value={selectedStepId}
                onChange={(event) => setSelectedStepId(event.target.value)}
              >
                <option value="">Choose a Step</option>
                {plan.steps
                  .filter(
                    (step) =>
                      !plan.queueItems.some((item) => item.stepId === step.id),
                  )
                  .map((step) => (
                    <option key={step.id} value={step.id}>
                      {step.instruction ||
                        `Step ${stepNumber.get(step.id) ?? ""}`}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                disabled={isBusy || isLocked || selectedStepId === ""}
                onClick={addQueueItem}
              >
                Add Queue Item
              </button>
            </div>
          ) : null}
          {undo === null || undo.kind === "window-remove" ? null : (
            <button
              type="button"
              disabled={isBusy || isLocked}
              onClick={undoChange}
            >
              Undo last Queue Item change
            </button>
          )}
        </section>

        <div aria-label="Daily Queue workflow">
          <p>
            Save for later keeps this editable. Finish review confirms the plan.
            Start queue makes it available to the student.
          </p>
          <button type="submit" disabled={isBusy || isLocked || disabled}>
            {busy === "draft" ? "Saving…" : "Save for later"}
          </button>
          <button
            type="button"
            disabled={isBusy || phase !== "draft" || disabled}
            onClick={() => void perform("ready")}
          >
            {busy === "ready" ? "Finishing review…" : "Finish review"}
          </button>
          <button
            type="button"
            disabled={isBusy || phase !== "ready" || disabled}
            onClick={() => void perform("active")}
          >
            {busy === "active" ? "Starting…" : "Start queue"}
          </button>
        </div>
      </form>
    </section>
  );
}
